import {
  createAsyncThunk,
  createSlice,
  type PayloadAction
} from "@reduxjs/toolkit"
import {
  type Category,
  type ClientListResponse,
  clientsService
} from "@/lib/api/services/clients"

// Define Client interface based on API response
export interface Client {
  id: number
  identifier: string
  firstName: string
  lastName: string
  email: string
  phoneMobile?: string
  title?: string
  dateOfBirth?: string
  maritalStatus?: string
  occupation?: string
  employmentStatus?: string
  grossIncome?: number
  netIncome?: number
  livingSituation?: string
  street?: string
  houseNumber?: string
  addressAddition?: string
  postalCode?: string
  cityPlaceOfResidence?: string
  federalState?: string
  country?: string
  nationality?: string
  healthInsurance?: string
  nursingCareInsuranceStatutoryPrivate?: string
  supplementaryInsurance?: string
  taxClass?: string
  taxAssessment?: string
  churchTax?: string
  socialSecurityId?: string
  taxId?: string
  mainAccountNumber?: string
  account1?: string
  account2?: string
  createdAt: string
  updatedAt: string
  isWizardUser?: boolean
  // Add other fields as needed based on your API response
}

interface ClientState {
  clients: Client[]
  currentClient: Client | null
  currentClientCategories: Category[]
  isLoading: boolean
  error: string | null
  totalCount: number
  currentPage: number
  totalPages: number
  limit: number
}

const initialState: ClientState = {
  clients: [],
  currentClient: null,
  currentClientCategories: [],
  isLoading: false,
  error: null,
  totalCount: 0,
  currentPage: 1,
  totalPages: 1,
  limit: 10
}

// Async thunks
export const fetchClients = createAsyncThunk(
  "clients/fetchClients",
  async (params: {
    page?: number
    limit?: number
    search?: string
    sortBy?: string
    sortOrder?: "asc" | "desc"
  }) => {
    try {
      // Token is managed by auth middleware and API client - no localStorage needed
      const response = await clientsService.getClients(params)

      if (response.success && response.data) {
        // Handle the actual API response structure
        // If the response.data has a nested data field, use it, otherwise use response.data directly
        const data = (
          "data" in response.data ? response.data.data : response.data
        ) as ClientListResponse
        const clients = data.clients || []

        // Extract pagination data - support both nested and flat structures
        const pagination = data.pagination || data
        const total = pagination.total ?? data.total ?? 0
        const page = pagination.page ?? data.page ?? 1
        const pages = pagination.pages ?? data.pages ?? 1
        const limit = pagination.limit ?? data.limit ?? 10

        return {
          clients,
          totalCount: total,
          currentPage: page,
          totalPages: pages,
          limit
        }
      }
      throw new Error("Failed to fetch clients")
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to fetch clients"
      throw new Error(errorMessage)
    }
  }
)

export const fetchClientById = createAsyncThunk(
  "clients/fetchClientById",
  async (clientId: string, { getState }) => {
    try {
      const state = getState() as {
        clients: ClientState
      }

      // Always fetch fresh data from API - no caching
      // Check if we have this client in the clients list to get the numeric ID
      const existingClient = state.clients.clients.find(
        (client: Client) => client.identifier === clientId
      )

      // If we found the client in store, use its numeric ID to fetch full details
      if (existingClient) {
        try {
          // Use the getClientDetails API with the numeric ID - always fetch fresh data
          const detailsResponse = await clientsService.getClientDetails(
            existingClient.id.toString()
          )

          if (detailsResponse.success && detailsResponse.data) {
            // Extract client from the response data
            // Handle potential nested data structure
            const dataToUse =
              "data" in detailsResponse.data
                ? detailsResponse.data.data
                : detailsResponse.data
            const clientData = (
              dataToUse &&
              typeof dataToUse === "object" &&
              "client" in dataToUse
                ? dataToUse.client
                : dataToUse
            ) as Client
            const categoriesData = (
              dataToUse &&
              typeof dataToUse === "object" &&
              "categories" in dataToUse
                ? dataToUse.categories
                : []
            ) as Category[]

            return { client: clientData, categories: categoriesData }
          }

          throw new Error("Invalid response format from getClientDetails")
        } catch (error) {
          console.error("Error fetching client details:", error)
          throw error
        }
      }

      // If not in store, we need to get the client first, then fetch details
      const response = await clientsService.getClientById(clientId)

      if (response.success && response.data) {
        // Now that we have the client, fetch full details with categories using numeric ID
        const numericId = response.data.id.toString()

        try {
          const detailsResponse =
            await clientsService.getClientDetails(numericId)

          if (detailsResponse.success && detailsResponse.data) {
            const dataToUse =
              "data" in detailsResponse.data
                ? detailsResponse.data.data
                : detailsResponse.data

            const clientData = (
              dataToUse &&
              typeof dataToUse === "object" &&
              "client" in dataToUse
                ? dataToUse.client
                : response.data
            ) as Client
            const categoriesData = (
              dataToUse &&
              typeof dataToUse === "object" &&
              "categories" in dataToUse
                ? dataToUse.categories
                : []
            ) as Category[]

            return { client: clientData, categories: categoriesData }
          }
        } catch (detailsError) {
          console.error(
            "Error fetching details after getClientById:",
            detailsError
          )
          // Fallback to client data without categories
          return { client: response.data, categories: [] }
        }

        // Fallback if details fetch fails
        return { client: response.data, categories: [] }
      }
      throw new Error("Failed to fetch client")
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to fetch client"
      throw new Error(errorMessage)
    }
  }
)

export const deleteClient = createAsyncThunk(
  "clients/deleteClient",
  async (clientId: string | number) => {
    try {
      const response = await clientsService.deleteClient(clientId.toString())

      if (response.success) {
        // Return numeric ID if clientId was a number, otherwise convert identifier to number
        return typeof clientId === "number"
          ? clientId
          : Number.parseInt(clientId, 10)
      }
      throw new Error("Failed to delete client")
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to delete client"
      throw new Error(errorMessage)
    }
  }
)

export const bulkDeleteClients = createAsyncThunk(
  "clients/bulkDeleteClients",
  async (ids: number[]) => {
    try {
      const response = await clientsService.bulkDelete(ids)
      if (response.success) {
        return ids
      }
      throw new Error("Failed to bulk delete clients")
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to bulk delete clients"
      throw new Error(errorMessage)
    }
  }
)

export const fetchInvitationForm = createAsyncThunk(
  "clients/fetchInvitationForm",
  async (token: string) => {
    try {
      const response = await clientsService.getInvitationForm(token)

      if (response.success && response.data) {
        const dataToUse =
          "data" in response.data ? response.data.data : response.data
        const categories =
          dataToUse &&
          typeof dataToUse === "object" &&
          "categories" in dataToUse
            ? (dataToUse.categories as Category[])
            : []

        return { categories }
      }
      throw new Error("Failed to fetch invitation form")
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to fetch invitation form"
      throw new Error(errorMessage)
    }
  }
)

const clientSlice = createSlice({
  name: "clients",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    setCurrentClient: (state, action: PayloadAction<Client | null>) => {
      state.currentClient = action.payload
    },
    clearClients: (state) => {
      state.clients = []
      state.currentClient = null
      state.currentClientCategories = []
      state.error = null
    }
  },
  extraReducers: (builder) => {
    // Fetch clients
    builder
      .addCase(fetchClients.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchClients.fulfilled, (state, action) => {
        state.isLoading = false
        state.clients = action.payload.clients
        state.totalCount = action.payload.totalCount
        state.currentPage = action.payload.currentPage
        state.totalPages = action.payload.totalPages
        state.limit = action.payload.limit
        state.error = null
      })
      .addCase(fetchClients.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message || "Failed to fetch clients"
      })

    // Fetch client by ID
    builder
      .addCase(fetchClientById.pending, (state) => {
        state.isLoading = true
        state.error = null
        // Clear previous client data to ensure fresh data is loaded
        // This prevents showing stale data while loading
        state.currentClient = null
        state.currentClientCategories = []
      })
      .addCase(fetchClientById.fulfilled, (state, action) => {
        state.isLoading = false
        state.currentClient = action.payload.client
        state.currentClientCategories = action.payload.categories
        state.error = null
      })
      .addCase(fetchClientById.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message || "Failed to fetch client"
      })

    // Delete client
    builder
      .addCase(deleteClient.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(deleteClient.fulfilled, (state, action) => {
        state.isLoading = false
        state.clients = state.clients.filter(
          (client) => client.id !== action.payload
        )
        if (state.currentClient?.id === action.payload) {
          state.currentClient = null
        }
        state.error = null
      })
      .addCase(deleteClient.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message || "Failed to delete client"
      })
      // Bulk delete clients
      .addCase(bulkDeleteClients.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(bulkDeleteClients.fulfilled, (state, action) => {
        state.isLoading = false
        const ids = action.payload
        state.clients = state.clients.filter(
          (client) => !ids.includes(client.id)
        )
        if (state.currentClient && ids.includes(state.currentClient.id)) {
          state.currentClient = null
        }
        state.error = null
      })
      .addCase(bulkDeleteClients.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message || "Failed to bulk delete clients"
      })

    // Fetch invitation form
    builder
      .addCase(fetchInvitationForm.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchInvitationForm.fulfilled, (state, action) => {
        state.isLoading = false
        state.currentClientCategories = action.payload.categories
        state.error = null
      })
      .addCase(fetchInvitationForm.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message || "Failed to fetch invitation form"
      })
  }
})

export const { clearError, setCurrentClient, clearClients } =
  clientSlice.actions
export default clientSlice.reducer
